import { useQuery } from '@tanstack/react-query';

import { UpcomingInvoiceResponse } from '@/api/invoices';
import { SubscriptionProductItem } from '@/api/subscriptions/types';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { ProrationBehavior, WithOptional } from '@/common/types';
import { pickTruthy } from '@/utils';

export const getStripeUpcomingInvoice = async (params: GetUpcomingInvoiceQueryParams, options?: RequestOptions) => {
    const payload = pickTruthy({
        items: 'items' in params ? params.items : undefined,
        subscription_id: 'subscriptionId' in params ? params.subscriptionId : undefined,
        proration_behavior: 'prorationBehavior' in params ? params.prorationBehavior : undefined,
        promotion_code: 'promotionCode' in params ? params.promotionCode : undefined,
        cancel_now: 'cancelNow' in params ? params.cancelNow : undefined,
    });
    return axios.get<UpcomingInvoiceResponse, UpcomingInvoiceResponse>(`/api/v2/subscriptions/upcoming-invoice`, {
        params: payload,
        ...options,
    });
};

type GetUpcomingInvoiceQueryParams = Cancellation | ExistingSubscription | NewSubscription | UpdatedSubscription;

type Cancellation = {
    // cancellation flow case
    subscriptionId: string;
    cancelNow: true;
};
type ExistingSubscription = {
    // regular unchanged upcoming invoice fetch case for existing subscription
    subscriptionId: string;
};
type NewSubscription = {
    // new susbscription case
    items: SubscriptionProductItem[];
    prorationBehavior: ProrationBehavior;
};
type UpdatedSubscription = {
    // existing subscription update case
    subscriptionId: string;
    items: SubscriptionProductItem[];
    prorationBehavior: ProrationBehavior;
    promotionCode?: string;
};

type UseUpcomingInvoiceParams =
    | WithOptional<Cancellation, 'subscriptionId'>
    | WithOptional<ExistingSubscription, 'subscriptionId'>
    | NewSubscription
    | WithOptional<UpdatedSubscription, 'subscriptionId'>;

type UseUpcomingInvoiceOptions = {
    config?: QueryConfig<typeof getStripeUpcomingInvoice>;
} & UseUpcomingInvoiceParams;

export const useUpcomingInvoice = ({
    config: { enabled = true, ...config } = {},
    ...params
}: UseUpcomingInvoiceOptions) => {
    const hasSubscriptionId = 'subscriptionId' in params && Boolean(params.subscriptionId);
    const hasItems = 'items' in params && !!params.items?.length;
    const isEnabled = enabled && (hasSubscriptionId || hasItems);
    return useQuery<UpcomingInvoiceResponse>({
        enabled: isEnabled,
        queryKey: ['stripe-updated-quantity-upcoming-invoice', params],
        // if it doesn't have either subscriptionId or items we would be !enabled
        queryFn: ({ signal }) => getStripeUpcomingInvoice(params as GetUpcomingInvoiceQueryParams, { signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        ...config,
    });
};
